import { useEffect, useRef, useState, memo } from 'react';
import { QuickstartTheme, VideoBackgroundConfig, isSeamlessMode, getLoadingVideoConfig, getCTAVideoConfig, getResolvedBackgroundMediaUrl } from '../types/theme';
import { QuickstartPhase } from '../QuickstartCoordinator';
import { isMobileDevice } from '../../../utility/utility';
import { useIsLandscape, useIsPortraitOrientation, useForcePortrait } from '../utils/responsiveHooks';

/**
 * Background mode determines which video/image to show
 */
export type BackgroundMode = 'loading' | 'matchmaking' | 'playing' | 'cta';

/**
 * Get background mode from quickstart phase
 */
export function getBackgroundModeFromPhase(phase: QuickstartPhase): BackgroundMode {
  switch (phase) {
    // Initial/matchmaking/connection phases - show loading video but no blur/vignette
    case 'page_viewed':
    case 'environment_check':
    case 'creating_lobby':
    case 'matchmaking':
    case 'match_found':
    case 'joining_broadcast':
    case 'preparing_player':
      return 'matchmaking';

    // Loading phases - show loading video (horizontal/landscape)
    case 'embedded_browser_blocked':
    case 'auto_redirect': // Auto-redirect uses loading video (horizontal), not CTA video (vertical)
      return 'loading';

    // Playing phases - hide background (game stream takes over)
    case 'playing_afk':
    case 'playing_active':
      return 'playing';

    // CTA/exit phases - show CTA video (or loading video fallback)
    case 'afk_timeout':
    case 'ad_timer_expired':
    case 'wishlist_early_exit':
    case 'cta_page':
    case 'thank_you':
    case 'auth_attempted':
    case 'auth_failed':
    case 'matchmaking_failed':
    case 'broadcast_join_failed':
    case 'error':
    default:
      return 'cta';
  }
}

interface SeamlessBackgroundProps {
  theme: QuickstartTheme;
  phase: QuickstartPhase;
  /** Optional: Override automatic mode detection */
  mode?: BackgroundMode;
  /** Whether to show the background (allows hiding during gameplay) */
  visible?: boolean;
  /** Z-index for layering (default: 0) */
  zIndex?: number;
}

/**
 * SeamlessBackground Component
 *
 * A persistent video/image background that lives OUTSIDE of AnimatePresence,
 * preventing the video from restarting when transitioning between phases.
 *
 * Architecture:
 * ```
 * <Quickstart>
 *   <SeamlessBackground /> ← This component (sibling, persists)
 *   <AnimatePresence>
 *     <LoadingOverlay />   ← Transparent overlay
 *     <PlayNowOverlay />
 *     <Player />           ← Game stream replaces video visually
 *     <CTAOverlay />
 *   </AnimatePresence>
 * </Quickstart>
 * ```
 *
 * Features:
 * - Video background with seamless looping
 * - Falls back to poster image or pageBackgroundImage
 * - Pauses video when not visible (performance)
 * - Handles video load errors gracefully
 * - Respects theme's video configuration
 */
export const SeamlessBackground = memo(function SeamlessBackground({
  theme,
  phase,
  mode: overrideMode,
  visible = true,
  zIndex = 0,
}: SeamlessBackgroundProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const previousVideoUrl = useRef<string | undefined>();
  const lastBufferLogTime = useRef(0);

  // Detect mobile devices (avoid hardware acceleration on mobile to save GPU memory)
  const isMobile = useRef(
    typeof navigator !== 'undefined' ? isMobileDevice() : false
  ).current;

  // Check if running in development mode
  const isDev = process.env.NODE_ENV === 'development';

  // Determine background mode from phase (or use override)
  const mode = overrideMode ?? getBackgroundModeFromPhase(phase);

  // Detect orientation and rotation needs based on mode
  const isLandscape = useIsLandscape();
  const isPortraitOrientation = useIsPortraitOrientation();
  const { shouldRotate: shouldRotateToPortrait } = useForcePortrait();

  // Apply rotation based on mode:
  // - loading/playing modes: force landscape (rotate 90deg on mobile portrait)
  // - cta mode: force portrait (rotate -90deg on mobile landscape)
  const shouldRotateToLandscape = (mode === 'loading' || mode === 'matchmaking' || mode === 'playing') && isLandscape && isPortraitOrientation;
  const shouldRotate = mode === 'cta' ? shouldRotateToPortrait : shouldRotateToLandscape;
  const rotationDegrees = mode === 'cta' ? -90 : 90;

  // Check if this theme uses seamless mode
  const seamlessEnabled = isSeamlessMode(theme);

  // Get the appropriate video config based on mode
  const getVideoConfig = (): VideoBackgroundConfig | undefined => {
    if (!seamlessEnabled) return undefined;

    switch (mode) {
      case 'loading':
      case 'matchmaking':
        return getLoadingVideoConfig(theme);
      case 'cta':
        return getCTAVideoConfig(theme);
      case 'playing':
        return getLoadingVideoConfig(theme);
      default:
        return undefined;
    }
  };

  const videoConfig = getVideoConfig();

  // Resolve per-device URL for the current config.
  // When CTA mode falls back to loadingBackgroundVideo (no trailer set),
  // use the loading per-device toggle instead of the trailer one.
  const getPerDeviceFlag = (): boolean => {
    if (mode === 'cta') {
      const hasTrailer = !!theme.trailerBackgroundVideo?.videoUrl || !!theme.trailerBackgroundVideo?.videoUrlIos;
      if (hasTrailer) return theme.usePerDeviceTrailerBackground ?? false;
      return theme.usePerDeviceLoadingBackground ?? false;
    }
    return theme.usePerDeviceLoadingBackground ?? false;
  };
  const usePerDevice = getPerDeviceFlag();
  const resolvedMediaUrl = getResolvedBackgroundMediaUrl(videoConfig, usePerDevice);

  // Detect if the resolved URL is a video or static image.
  // Default to video for backwards compatibility (existing campaigns store video URLs here).
  // Only classify as image when the URL has a clear image extension.
  const isImageUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return /\.(jpg|jpeg|png|gif|webp|avif|svg|bmp)(\?|$)/i.test(url);
  };
  const resolvedIsVideo = resolvedMediaUrl ? !isImageUrl(resolvedMediaUrl) : false;

  // Get fallback background image
  const fallbackImage = videoConfig?.posterImage ?? theme.pageBackgroundImage;

  // Reset video loaded state when resolved media URL changes
  useEffect(() => {
    if (resolvedMediaUrl && resolvedMediaUrl !== previousVideoUrl.current) {
      if (isDev) {
        console.log('[SeamlessBackground] Media URL changed:', previousVideoUrl.current, '→', resolvedMediaUrl);
      }
      setVideoLoaded(false);
      setVideoError(false);
      previousVideoUrl.current = resolvedMediaUrl;
    }
  }, [resolvedMediaUrl, isDev]);

  // Should we show the video element? Only when URL exists, is a video format, and hasn't errored
  const shouldShowVideo = seamlessEnabled && resolvedMediaUrl && resolvedIsVideo && !videoError;

  // Should we show a static image as the primary background? (non-video URL)
  const shouldShowStaticImage = seamlessEnabled && resolvedMediaUrl && !resolvedIsVideo;

  // Should the background be visible? (hide during active gameplay)
  const isVisible = visible && mode !== 'playing';

  // Handle video playback based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isVisible && shouldShowVideo) {
      // Resume video when visible with recovery logic
      const attemptPlay = async () => {
        try {
          // Check if video is in a stuck/stalled state
          if (video.readyState < 2) {
            if (isDev) {
              console.log('[SeamlessBackground] Video not ready, waiting for loadeddata event');
            }
            return;
          }

          // Ensure video is not paused at the very end (can cause frozen frame)
          if (video.currentTime >= video.duration - 0.1 && video.duration > 0) {
            if (isDev) {
              console.log('[SeamlessBackground] Video at end, restarting from beginning');
            }
            video.currentTime = 0;
          }

          await video.play();
          if (isDev) {
            console.log('[SeamlessBackground] Video playing successfully');
          }
        } catch (err) {
          if (isDev) {
            console.warn('[SeamlessBackground] Video play failed:', err instanceof Error ? err.name : 'unknown', err instanceof Error ? err.message : '');
          }
          // Don't set error here - video might play on user interaction
        }
      };

      attemptPlay();

      // Add event listeners to detect and recover from stalled states
      const handleStalled = () => {
        if (isDev) {
          console.warn('[SeamlessBackground] Video stalled, attempting recovery');
        }
        if (video.readyState < 2) {
          video.load(); // Force reload
        } else {
          attemptPlay(); // Try playing again
        }
      };

      const handleWaiting = () => {
        // Throttle buffering logs to avoid console spam (max once per 2 seconds)
        const now = Date.now();
        if (isDev && now - lastBufferLogTime.current > 2000) {
          console.log('[SeamlessBackground] Video buffering...');
          lastBufferLogTime.current = now;
        }
      };

      const handleCanPlay = () => {
        if (video.paused && isVisible) {
          if (isDev) {
            console.log('[SeamlessBackground] Video can play, resuming');
          }
          attemptPlay();
        }
      };

      video.addEventListener('stalled', handleStalled);
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('canplay', handleCanPlay);

      return () => {
        video.removeEventListener('stalled', handleStalled);
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('canplay', handleCanPlay);
      };
    } else {
      // Pause video when hidden (performance optimization)
      video.pause();
    }
  }, [isVisible, shouldShowVideo, isDev]);

  // Set playback rate from config
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set playback rate, defaulting to 1.0 (normal speed)
    const targetRate = videoConfig?.playbackRate ?? 1.0;

    // Warn if playback rate is non-standard (can cause jitter)
    if (isDev && targetRate !== 1.0) {
      console.warn('[SeamlessBackground] Non-standard playback rate:', targetRate, '- may cause jitter. Recommended: 1.0');
    }

    video.playbackRate = targetRate;
  }, [videoConfig?.playbackRate, isDev]);

  // Handle video load success
  const handleVideoLoaded = () => {
    const video = videoRef.current;
    if (video && isDev) {
      console.log('[SeamlessBackground] Video loaded:', {
        duration: video.duration,
        dimensions: `${video.videoWidth}x${video.videoHeight}`,
        readyState: video.readyState,
      });

      // Diagnostic warnings for common jitter causes (only in development)
      if (video.videoWidth > 1920 || video.videoHeight > 1080) {
        console.warn('[SeamlessBackground] ⚠️ Video resolution too high - expect jitter on some devices. Recommended: 1920x1080 or lower');
      }

      // Check if video is likely 60fps (duration/frame count heuristic)
      // Note: HTML5 video doesn't expose frame rate directly, but high bitrate + high resolution often indicates 60fps
      if ((video.videoWidth * video.videoHeight) > (1920 * 1080) * 0.9) {
        console.warn('[SeamlessBackground] ⚠️ High resolution video may cause jitter. For smooth background video, use: 1080p @ 30fps, H.264, 2-4 Mbps');
      }
    }
    setVideoLoaded(true);
    setVideoError(false);
  };

  // Handle video load error
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    if (isDev) {
      console.error('[SeamlessBackground] Video error:', {
        error: video.error?.message,
        code: video.error?.code,
        src: videoConfig?.videoUrl,
      });
    }
    setVideoError(true);
  };

  // If not in seamless mode, render traditional static background
  if (!seamlessEnabled) {
    if (!theme.pageBackgroundImage) return null;

    return (
      <div
        className='fixed inset-0'
        style={{
          zIndex,
          backgroundImage: `url(${theme.pageBackgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
        aria-hidden='true'
      />
    );
  }

  return (
    <div
      className='fixed overflow-hidden'
      style={
        shouldRotate ? {
          // Rotated mode: swap dimensions and rotate based on phase
          // loading/playing: 90deg (landscape), cta: -90deg (portrait)
          position: 'fixed',
          left: '50%',
          top: '50%',
          width: '100dvh',
          height: '100dvw',
          transform: `translate(-50%, -50%) rotate(${rotationDegrees}deg)`,
          transformOrigin: 'center center',
          zIndex,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 300ms ease-in-out',
          pointerEvents: 'none',
        } : {
          // Normal mode
          inset: 0,
          zIndex,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 300ms ease-in-out',
          pointerEvents: 'none',
        }
      }
      aria-hidden='true'
    >
      {/* Video Background */}
      {shouldShowVideo && resolvedMediaUrl && (
        <video
          ref={videoRef}
          className='absolute inset-0 h-full w-full object-contain'
          src={resolvedMediaUrl}
          poster={videoConfig?.posterImage}
          loop={videoConfig?.loop ?? true}
          muted={videoConfig?.muted ?? true}
          playsInline
          autoPlay
          preload='auto'
          onLoadedData={handleVideoLoaded}
          onError={handleVideoError}
          style={{
            // Hide video until loaded to prevent flash
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 200ms ease-in, filter 300ms ease-in-out',
            filter: mode === 'loading' || mode === 'cta' ? 'blur(4px)' : 'blur(0px)',
            // Hardware acceleration hints for smoother playback (desktop only to save GPU memory on mobile)
            ...(isMobile ? {} : {
              willChange: 'transform, opacity, filter',
              transform: 'translateZ(0)', // Force GPU layer
              backfaceVisibility: 'hidden' as const,
              perspective: 1000,
            }),
          }}
        />
      )}

      {/* Static Image Background (when resolved URL is an image, not video) */}
      {shouldShowStaticImage && resolvedMediaUrl && (
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: `url(${resolvedMediaUrl})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: mode === 'loading' || mode === 'cta' ? 'blur(4px)' : 'blur(0px)',
            transition: 'filter 300ms ease-in-out',
          }}
        />
      )}

      {/* Fallback Image (shown while video loads or on error, or when no primary media) */}
      {fallbackImage && !shouldShowStaticImage && (!shouldShowVideo || !videoLoaded) && (
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: `url(${fallbackImage})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: mode === 'loading' || mode === 'cta' ? 'blur(4px)' : 'blur(0px)',
            transition: 'filter 300ms ease-in-out',
          }}
        />
      )}

      {/* Vignette overlay - darkens edges for focus on center content */}
      {(mode === 'loading' || mode === 'cta') && (
        <div
          className='pointer-events-none absolute inset-0'
          style={{
            background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.7) 100%)',
          }}
        />
      )}

      {/* Gradient overlay for better text readability */}
      <div
        className='absolute inset-0'
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.4) 100%)',
        }}
      />
    </div>
  );
});

/**
 * Hook to manage seamless background visibility
 * Use this in parent component to control when background shows/hides
 */
export function useSeamlessBackgroundVisibility(phase: QuickstartPhase): boolean {
  const mode = getBackgroundModeFromPhase(phase);

  // Hide during active gameplay (game stream is visible)
  // Show during loading and CTA phases
  return mode !== 'playing';
}

export default SeamlessBackground;

